import React from 'react'
import { XStack, YStack } from 'tamagui'

import { Card } from '@newComponents/Card'
import { Icon } from '@newComponents/Icon'
import { Paragraph } from '@newComponents/Text'
import { Link } from '@newComponents/Link'

import { DocSection } from '../shared/DocSection'
import { DocStep } from '../shared/DocStep'

const CalendarFeeds = () => (
  <>
    <Card gap='$4'>
      <Paragraph size='$5'>
        Every Guild on Guild has an iCal calendar feed. You can subscribe to
        these feeds in Apple Calendar, Google Calendar, Outlook, or any
        calendar app that supports the iCal format. Feeds update automatically
        as Events are added, modified, or canceled, so your calendar always
        reflects the latest schedule.
      </Paragraph>
      <Paragraph size='$5'>
        Calendar feeds are a simple, privacy-friendly way to stay up to date.
        No account connection or OAuth is required — just copy a URL and paste
        it into your calendar app.
      </Paragraph>
      {/* TODO: Capture screenshot of the calendar subscription dialog showing iCal feed URL and calendar app options */}
    </Card>

    <Card gap='$4'>
      <DocSection id='subscribing-to-a-feed' title='Subscribing to a Feed'>
        <DocStep
          number={1}
          title='Open the calendar subscription dialog'
          description='On any Guild page, click the calendar icon to open the subscription options.'
        />
        <DocStep
          number={2}
          title='Choose your calendar app'
          description='Select Google Calendar or Apple Calendar to subscribe directly. You can also copy the iCal feed URL to add it manually in any calendar app that supports iCal, such as Outlook or Thunderbird.'
        />
        <Paragraph size='$5'>
          Once subscribed, your calendar app will periodically check the feed URL
          for updates. Most calendar apps refresh every few hours, though some
          let you configure the refresh interval.
        </Paragraph>
      </DocSection>
    </Card>

    <Card gap='$4'>
      <DocSection id='feed-types' title='Feed Types'>
        <YStack gap='$3'>
          <XStack gap='$3' alignItems='flex-start'>
            <Icon name='people-community' size='$5' color='$color' flexShrink={0} marginTop={2} />
            <Paragraph size='$5'>
              <Paragraph size='$5' fontWeight='$7'>Guild-level feeds</Paragraph>{' '}
              include all upcoming and future Events for that Guild. When a new{' '}
              <Link href='/docs/events/overview'>Event</Link> is created or an
              existing one is updated, the feed reflects the change automatically.
              This is the best option for staying up to date with everything a Guild
              has planned.
            </Paragraph>
          </XStack>
          <XStack gap='$3' alignItems='flex-start'>
            <Icon name='calendar' size='$5' color='$color' flexShrink={0} marginTop={2} />
            <Paragraph size='$5'>
              <Paragraph size='$5' fontWeight='$7'>
                Individual Event feeds
              </Paragraph>{' '}
              are also available if you only want to track a specific Event. This is
              useful when you're attending a single Event from a Guild but don't need
              their full calendar.
            </Paragraph>
          </XStack>
          <XStack gap='$3' alignItems='flex-start'>
            <Icon name='person' size='$5' color='$color' flexShrink={0} marginTop={2} />
            <Paragraph size='$5'>
              <Paragraph size='$5' fontWeight='$7'>Per-user feeds</Paragraph> show
              all Events you've RSVPed to across every Guild. This is a personal feed
              that aggregates your Event schedule in one place, regardless of which
              Guild is hosting.
            </Paragraph>
          </XStack>
        </YStack>
      </DocSection>
    </Card>

    <Card gap='$4'>
      <DocSection id='feeds-vs-google-calendar' title='Calendar Feeds vs. Google Calendar Sync'>
        <Paragraph size='$5'>
          Calendar feeds and{' '}
          <Link href='/docs/guilds/google-calendar'>Google Calendar sync</Link>{' '}
          both put Events on your calendar, but they work differently:
        </Paragraph>
        <YStack gap='$3'>
          <XStack gap='$3' alignItems='flex-start'>
            <Icon name='calendar-multiple' size='$5' color='$color' flexShrink={0} marginTop={2} />
            <Paragraph size='$5'>
              <Paragraph size='$5' fontWeight='$7'>Calendar Feeds</Paragraph> are
              read-only subscriptions. They don't require connecting a Google account
              and work with any calendar app. They show all of a Guild's Events (or
              all your RSVPed Events), and your calendar app checks the feed URL for
              updates on its own schedule.
            </Paragraph>
          </XStack>
          <XStack gap='$3' alignItems='flex-start'>
            <Icon name='calendar' size='$5' color='$color' flexShrink={0} marginTop={2} />
            <Paragraph size='$5'>
              <Paragraph size='$5' fontWeight='$7'>Google Calendar sync</Paragraph>{' '}
              is a two-way integration. It requires connecting your Google account via
              OAuth and only adds Events you RSVP to. Changes are pushed to your
              calendar instantly rather than waiting for a feed refresh. Canceling an
              RSVP removes the calendar entry automatically.
            </Paragraph>
          </XStack>
        </YStack>
        <Paragraph size='$5'>
          If you want a simple subscription to a Guild's full Event calendar, use
          a Calendar Feed. If you want your personal RSVP schedule to stay in
          sync in real time, connect Google Calendar.
        </Paragraph>
      </DocSection>
    </Card>
  </>
)

export default CalendarFeeds
